import CardMembershipIcon from '@mui/icons-material/CardMembership';
import MessageIcon from '@mui/icons-material/Message';
import MovieIcon from '@mui/icons-material/Movie';
import PostAddIcon from '@mui/icons-material/PostAdd';
import TollIcon from '@mui/icons-material/Toll';
import { Box, Divider, LinearProgress, Stack, Typography, useTheme } from '@mui/material';
import dinero from 'dinero.js';
import moment from 'moment';
import React from 'react';
import { SubscriberMetricsFansSubscriptionsAndEarningsGroupByMonth } from '../../../../../../hooks/useSextforceMetricsAccountSubscriptionsAndEarningsGroupByMonth';

interface MonthlyPerformanceCardProps {
    month: SubscriberMetricsFansSubscriptionsAndEarningsGroupByMonth;
    maxEarnings: number;
    maxSubs: number;
    maxSpenders: number;
}

const MonthlyPerformanceCard: React.FC<MonthlyPerformanceCardProps> = ({ month, maxEarnings, maxSubs, maxSpenders }) => {
    const theme = useTheme();

    // Function to format currency using dinero.js
    const formatCurrency = (amount: number) => {
        // Convert amount to cents for dinero.js (which works with integer values)
        const amountInCents = Math.round(amount * 100);
        const money = dinero({ amount: amountInCents, currency: 'USD' });

        // Format with dinero.js
        return money.toFormat();
    };

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {moment(`${month.year}-${month.month}-01`).format('MMMM YYYY')}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                        Earnings:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(month.amountNet)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                        Subscribers:
                    </Typography>
                    <Typography variant="body2">{month.subsCount.toLocaleString()}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                        Spenders:
                    </Typography>
                    <Typography variant="body2">{month.spendersCount.toLocaleString()}</Typography>
                </Box>

                {/* Revenue Source Breakdown */}
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Revenue Sources:
                </Typography>

                {/* Subscriptions */}
                <Box sx={{ mt: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CardMembershipIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="textSecondary">
                                Subscriptions:
                            </Typography>
                        </Box>
                        <Typography variant="caption">{formatCurrency(month.subscriptionsAmountNet)}</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={month.amountNet > 0 ? (month.subscriptionsAmountNet / month.amountNet) * 100 : 0}
                        sx={{
                            height: 4,
                            borderRadius: 1,
                            mb: 0.75,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.primary.light,
                            },
                        }}
                    />
                </Box>

                {/* Messages */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <MessageIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="textSecondary">
                                Messages/PPVs:
                            </Typography>
                        </Box>
                        <Typography variant="caption">{formatCurrency(month.messagesAmountNet)}</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={month.amountNet > 0 ? (month.messagesAmountNet / month.amountNet) * 100 : 0}
                        sx={{
                            height: 4,
                            borderRadius: 1,
                            mb: 0.75,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.secondary.light,
                            },
                        }}
                    />
                </Box>

                {/* Tips */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TollIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="textSecondary">
                                Tips:
                            </Typography>
                        </Box>
                        <Typography variant="caption">{formatCurrency(month.tipsAmountNet)}</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={month.amountNet > 0 ? (month.tipsAmountNet / month.amountNet) * 100 : 0}
                        sx={{
                            height: 4,
                            borderRadius: 1,
                            mb: 0.75,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.error.light,
                            },
                        }}
                    />
                </Box>

                {/* Posts */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PostAddIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="textSecondary">
                                Posts:
                            </Typography>
                        </Box>
                        <Typography variant="caption">{formatCurrency(month.postsAmountNet)}</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={month.amountNet > 0 ? (month.postsAmountNet / month.amountNet) * 100 : 0}
                        sx={{
                            height: 4,
                            borderRadius: 1,
                            mb: 0.75,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.warning.light,
                            },
                        }}
                    />
                </Box>

                {/* Streams */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <MovieIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" color="textSecondary">
                                Streams:
                            </Typography>
                        </Box>
                        <Typography variant="caption">{formatCurrency(month.streamsAmountNet)}</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={month.amountNet > 0 ? (month.streamsAmountNet / month.amountNet) * 100 : 0}
                        sx={{
                            height: 4,
                            borderRadius: 1,
                            mb: 0.75,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.info.light,
                            },
                        }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default MonthlyPerformanceCard;
